import React, { useEffect, useState } from 'react';
import { BOAPI } from '../service/UsersService';
import utils from '../utils/utils';
import AnnouncementEditModal from './AnnouncementEditModal';
import AnnouncementsCalendar from './AnnouncementsCalendar';

// תמונה יכולה להיות קישור חיצוני מלא (http/https) ולא רק נתיב /uploads/...
// מקומי - ראו הערה זהה ב-AnnouncementEditModal.jsx
const fullUrl = (path) => (!path ? null : /^https?:\/\//i.test(path) ? path : `${utils.BaseUrl}${path}`);

// displayDate חוזר מהשרת כ-datetime (ISO, UTC) - לא מותר להשוות אותו כמחרוזת
// גולמית (String(d).slice(0,10)) מול תאריך מקומי, כי SQL Server (useUTC:false,
// ראו sqlDb.ts) בונה אותו כחצות *מקומי* ואז JSON.stringify ממיר ל-UTC -
// בישראל (UTC+3) זה זז יום אחד אחורה. toLocaleDateString/getFullYear וכו'
// ממירים בחזרה לזמן מקומי של הדפדפן ומתקנים את זה - בדיוק כמו fmtDate למטה
const todayStr = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};
const localDateStr = (d) => {
  const dt = new Date(d);
  return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`;
};
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString('he-IL', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '—');
const isToday = (d) => !!d && localDateStr(d) === todayStr();

// טאב "הודעות/פרסומות" - התוכן שמוצג במסך הפתיחה של האפליקציה (מסך
// "סטורי" לכמה שניות לפני הכניסה למאגר בתי העסק, ראו client/app/
// announcements.tsx). ממוין לפי sortOrder - זה גם סדר ההצגה בפועל
// באפליקציה. "מחיקה"/"שחזור" = השבתה/הפעלה, לא DELETE אמיתי - אותה
// מוסכמה בדיוק כמו בתי עסק
const AnnouncementsTab = () => {
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  const [toggleBusyId, setToggleBusyId] = useState(null);
  const [editingAnnouncement, setEditingAnnouncement] = useState(null);
  // creating: false | { displayDate? } - האובייקט הוא ה-announcement ההתחלתי של המודל
  // (displayDate מוגדר מראש כשלוחצים על יום בלוח השנה)
  const [creating, setCreating] = useState(false);
  const [viewMode, setViewMode] = useState(() => {
    try { return localStorage.getItem('announcements-view') === 'table' ? 'table' : 'calendar'; } catch { return 'calendar'; }
  });

  const changeView = (mode) => {
    setViewMode(mode);
    try { localStorage.setItem('announcements-view', mode); } catch { /* localStorage חסום - לא קריטי */ }
  };

  useEffect(() => {
    loadAnnouncements();
  }, []);

  const loadAnnouncements = async () => {
    try {
      setLoading(true);
      setError('');
      const resp = await BOAPI.fetchAnnouncements();
      if (resp?.[0]?.[0]?.code === 0) setAnnouncements(resp[1] || []);
      else setError(resp?.[0]?.[0]?.msg || 'שגיאה בטעינת ההודעות');
    } catch {
      setError('שגיאה בתקשורת עם השרת');
    } finally {
      setLoading(false);
    }
  };

  const showSuccess = (msg) => {
    setSuccessMsg(msg);
    setTimeout(() => setSuccessMsg(''), 4000);
  };

  const toggleActive = async (announcement) => {
    setToggleBusyId(announcement.id);
    setError('');
    try {
      const resp = await BOAPI.toggleAnnouncementActive(announcement.id);
      if (resp?.[0]?.[0]?.code === 0) {
        const active = !!resp[1]?.[0]?.active;
        setAnnouncements((prev) => prev.map((a) => (a.id === announcement.id ? { ...a, active } : a)));
      } else {
        setError(resp?.[0]?.[0]?.msg || 'העדכון נכשל');
      }
    } catch {
      setError('שגיאה בתקשורת עם השרת');
    } finally {
      setToggleBusyId(null);
    }
  };

  return (
    <div style={s.container}>
      <div style={s.headerRow}>
        <h2 style={s.title}>הודעות/פרסומות</h2>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={s.totalBadge}>{announcements.length} הודעות</span>
          <div style={s.viewToggle}>
            <button style={{ ...s.viewBtn, ...(viewMode === 'calendar' ? s.viewBtnActive : {}) }} onClick={() => changeView('calendar')}>📅 לוח שנה</button>
            <button style={{ ...s.viewBtn, ...(viewMode === 'table' ? s.viewBtnActive : {}) }} onClick={() => changeView('table')}>☰ טבלה</button>
          </div>
          <button style={s.addBtn} onClick={() => setCreating({})}>+ הודעה חדשה</button>
        </div>
      </div>

      <p style={s.hint}>הודעה מוצגת באפליקציה רק ביום שנקבע לה ("תאריך תצוגה") ורק אם היא פעילה - לפי "סדר תצוגה", לכמה שניות לפני הכניסה למאגר בתי העסק.</p>

      {error && (
        <div style={s.errorBanner}>❌ {error}
          <button style={s.closeErr} onClick={() => setError('')}>✕</button>
        </div>
      )}
      {successMsg && <div style={s.successBanner}>✅ {successMsg}</div>}

      {loading ? (
        <div style={s.center}>טוען...</div>
      ) : viewMode === 'calendar' ? (
        <AnnouncementsCalendar
          announcements={announcements}
          onEdit={setEditingAnnouncement}
          onCreateOnDate={(date) => setCreating({ displayDate: date })}
        />
      ) : announcements.length === 0 ? (
        <div style={s.emptyState}>
          <div style={{ fontSize: 48 }}>📣</div>
          <p style={{ color: '#697386', fontWeight: 600 }}>אין עדיין הודעות/פרסומות</p>
        </div>
      ) : (
        <div style={s.tableWrapper}>
          <table style={s.table}>
            <thead>
              <tr style={s.headerRow2}>
                <th style={s.th}></th>
                <th style={s.th}>כותרת</th>
                <th style={s.th}>תוכן</th>
                <th style={s.th}>תאריך תצוגה</th>
                <th style={s.th}>סדר תצוגה</th>
                <th style={s.th}>סטטוס</th>
                <th style={s.th}>פעולות</th>
              </tr>
            </thead>
            <tbody>
              {announcements.map((a) => (
                <tr key={a.id} style={s.row}>
                  <td style={s.td}>
                    {a.imageUrl ? (
                      <img src={fullUrl(a.imageUrl)} alt="" style={s.thumb} />
                    ) : (
                      <div style={s.thumbPlaceholder}>📣</div>
                    )}
                  </td>
                  <td style={s.td}>
                    <button style={s.nameLink} onClick={() => setEditingAnnouncement(a)}>{a.title}</button>
                  </td>
                  <td style={s.td}>
                    <span style={s.messagePreview}>{a.message || '-'}</span>
                  </td>
                  <td style={s.td}>
                    {fmtDate(a.displayDate)}
                    {isToday(a.displayDate) && <span style={s.todayBadge}>מוצג היום</span>}
                  </td>
                  <td style={s.td}>{a.sortOrder}</td>
                  <td style={s.td}>
                    <span style={{ ...s.statusBadge, ...(a.active ? s.statusActive : s.statusInactive) }}>
                      {a.active ? 'פעיל' : 'מוסתר'}
                    </span>
                  </td>
                  <td style={s.td}>
                    <div style={s.rowActions}>
                      <button style={s.editBtn} onClick={() => setEditingAnnouncement(a)}>✏️ עריכה</button>
                      <button
                        style={a.active ? s.deactivateBtn : s.activateBtn}
                        onClick={() => toggleActive(a)}
                        disabled={toggleBusyId === a.id}
                      >
                        {toggleBusyId === a.id ? '...' : a.active ? 'מחיקה' : 'שחזור'}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <AnnouncementEditModal
        announcement={editingAnnouncement}
        onClose={() => setEditingAnnouncement(null)}
        onSaved={(updated) => {
          if (!updated) return;
          setAnnouncements((prev) => prev.map((a) => (a.id === updated.id ? { ...a, ...updated } : a)));
          setEditingAnnouncement(null);
          showSuccess(`"${updated.title}" נשמר בהצלחה`);
        }}
      />

      {creating && (
        <AnnouncementEditModal
          announcement={creating}
          onClose={() => setCreating(false)}
          onSaved={(created) => {
            if (!created) return;
            setAnnouncements((prev) => [...prev, created].sort((a, b) => a.sortOrder - b.sortOrder));
            setCreating(false);
            showSuccess(`"${created.title}" נוסף בהצלחה`);
          }}
        />
      )}
    </div>
  );
};

const s = {
  container:    { padding: 20, direction: 'rtl', fontFamily: 'Arial, sans-serif' },
  headerRow:    { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6, flexWrap: 'wrap', gap: 12 },
  title:        { margin: 0, color: '#1a1f36', fontSize: 24, fontWeight: 'bold' },
  totalBadge:   { background: '#e8eaf6', color: '#1f6feb', borderRadius: 12, padding: '4px 12px', fontSize: 13, fontWeight: 700 },
  viewToggle:   { display: 'flex', border: '1.5px solid #c7d7fb', borderRadius: 10, overflow: 'hidden' },
  viewBtn:      { padding: '9px 14px', background: '#fff', color: '#1f6feb', border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 700 },
  viewBtnActive:{ background: '#1f6feb', color: '#fff' },
  addBtn:       { padding: '9px 16px', background: '#1f6feb', color: '#fff', border: 'none', borderRadius: 10, cursor: 'pointer', fontSize: 13, fontWeight: 700 },
  hint:         { color: '#8792a2', fontSize: 12.5, marginBottom: 18, lineHeight: 1.6 },
  center:       { padding: 40, textAlign: 'center', color: '#999' },
  errorBanner:  { display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#ffebee', color: '#c62828', padding: 14, borderRadius: 6, marginBottom: 16 },
  closeErr:     { background: 'transparent', border: 'none', cursor: 'pointer', color: '#c62828', fontSize: 18 },
  successBanner:{ background: '#e8f5e9', color: '#2e7d32', padding: 12, borderRadius: 8, marginBottom: 16, fontSize: 13 },
  emptyState:   { textAlign: 'center', padding: '60px 0' },

  tableWrapper: { overflowX: 'auto', borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.08)' },
  table:        { width: '100%', borderCollapse: 'collapse', backgroundColor: 'white' },
  headerRow2:   { backgroundColor: '#f5f7fa', borderBottom: '2px solid #e3e8ee' },
  th:           { padding: '14px 16px', textAlign: 'right', fontWeight: 700, color: '#4f566b', fontSize: 13, borderBottom: '2px solid #e3e8ee' },
  row:          { borderBottom: '1px solid #f0f2f5' },
  td:           { padding: '10px 16px', color: '#4f566b', fontSize: 13, textAlign: 'right' },
  thumb:        { width: 46, height: 46, borderRadius: 8, objectFit: 'cover' },
  thumbPlaceholder: { width: 46, height: 46, borderRadius: 8, background: '#EEF2FF', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18 },
  nameLink:     { background: 'none', border: 'none', cursor: 'pointer', color: '#1f6feb', fontWeight: 700, fontSize: 13, padding: 0, textAlign: 'right' },
  messagePreview: { display: 'inline-block', maxWidth: 260, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', verticalAlign: 'bottom' },
  statusBadge:  { display: 'inline-block', borderRadius: 12, padding: '3px 10px', fontSize: 11, fontWeight: 700 },
  statusActive: { background: '#e8f5e9', color: '#2e7d32' },
  todayBadge:   { display: 'inline-block', marginRight: 8, background: '#eef4ff', color: '#1f6feb', borderRadius: 10, padding: '2px 8px', fontSize: 10.5, fontWeight: 700 },
  statusInactive:{ background: '#fff1f1', color: '#d93838' },
  rowActions:   { display: 'flex', gap: 6, flexWrap: 'wrap' },
  editBtn:      { padding: '6px 12px', background: '#EEF2FF', color: '#1f6feb', border: '1px solid #c7d7fb', borderRadius: 8, cursor: 'pointer', fontSize: 12, fontWeight: 700 },
  activateBtn:  { padding: '6px 12px', background: '#e8f5e9', color: '#2e7d32', border: '1px solid #c8e6c9', borderRadius: 8, cursor: 'pointer', fontSize: 12, fontWeight: 700 },
  deactivateBtn:{ padding: '6px 12px', background: '#fff1f1', color: '#d93838', border: '1px solid #fbc4c4', borderRadius: 8, cursor: 'pointer', fontSize: 12, fontWeight: 700 },
};

export default AnnouncementsTab;
